import dayjs from 'dayjs'

import { CARD_TYPE } from './constants'
import { formatDateFR, formatPax } from './format'

export const hasDailyStock = (carte) =>
  Array.isArray(carte?.daily_inventory) && carte.daily_inventory.length > 0

export const hasDailyStockFromForm = (form) =>
  Boolean(form?.use_daily_stock) ||
  (Array.isArray(form?.daily_inventory) && form.daily_inventory.length > 0)

export const getStockAccessMessage = (carte, variant = 'admin', t) => {
  const isDaily = hasDailyStock(carte)
  if (t) {
    if (variant === 'client') {
      return t(isDaily ? 'cartesAcces.shared.stockAccess.clientDaily' : 'cartesAcces.shared.stockAccess.clientInterval')
    }
    return t(isDaily ? 'cartesAcces.shared.stockAccess.adminDaily' : 'cartesAcces.shared.stockAccess.adminInterval')
  }
  if (isDaily) {
    return variant === 'client'
      ? 'Accès valable pour un jour de votre choix dans la période'
      : 'Le client aura accès pour un seul jour selon son choix'
  }
  return variant === 'client'
    ? 'Accès valable chaque jour pendant toute la période'
    : "Le client aura accès chaque jour dans l'interval"
}

export const getStockAccessMessageFromForm = (form, variant = 'admin', t) => {
  const isDaily = hasDailyStockFromForm(form)
  if (t) {
    if (variant === 'client') {
      return t(isDaily ? 'cartesAcces.shared.stockAccess.clientDaily' : 'cartesAcces.shared.stockAccess.clientInterval')
    }
    return t(isDaily ? 'cartesAcces.shared.stockAccess.adminDaily' : 'cartesAcces.shared.stockAccess.adminInterval')
  }
  if (isDaily) {
    return variant === 'client'
      ? 'Accès valable pour un jour de votre choix dans la période'
      : 'Le client aura accès pour un seul jour selon son choix'
  }
  return variant === 'client'
    ? 'Accès valable chaque jour pendant toute la période'
    : "Le client aura accès chaque jour dans l'interval"
}

export const formatAccesCapacity = (carte, t) => {
  const adults = Number(carte?.max_adults ?? 0)
  const children = Number(carte?.max_children ?? 0)
  const parts = []
  if (adults > 0) {
    parts.push(t ? t('cartesAcces.shared.capacity.maxAdults', { count: adults }) : `Max ${adults} ad.`)
  }
  if (children > 0) {
    parts.push(t ? t('cartesAcces.shared.capacity.maxChildren', { count: children }) : `Max ${children} enf.`)
  }
  if (!parts.length) return '—'
  return parts.join(' · ')
}

export const formatCarteCapacity = (carte, t) => {
  if (!carte) return '—'
  if (carte.type === CARD_TYPE.ACCES) return formatAccesCapacity(carte, t)
  const pax = formatPax(carte.min_pax, carte.max_pax)
  return t ? t('cartesAcces.shared.capacity.persons', { pax }) : `${pax} pers.`
}

export const toDateInputValue = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '')

export const getMinBookableDateForCarte = (carte) => {
  const today = dayjs().startOf('day')
  if (carte?.type !== CARD_TYPE.ACCES) {
    return carte?.start_date ? toDateInputValue(carte.start_date) : today.format('YYYY-MM-DD')
  }
  const start = carte?.start_date ? dayjs(carte.start_date).startOf('day') : today
  const min = start.isAfter(today) ? start : today
  return min.format('YYYY-MM-DD')
}

export const clampCarteBookableDate = (carte, preferred) => {
  if (!carte || carte.type !== CARD_TYPE.ACCES) {
    return preferred ? toDateInputValue(preferred) : ''
  }
  const min = getMinBookableDateForCarte(carte)
  if (!preferred) return min
  const picked = toDateInputValue(preferred)
  if (!picked) return min
  if (dayjs(picked).isBefore(dayjs(min), 'day')) return min
  const max = carte.end_date ? toDateInputValue(carte.end_date) : null
  if (max && dayjs(picked).isAfter(dayjs(max), 'day')) return max
  return picked
}

export const isDateInCarteInterval = (carte, date) => {
  if (!date || !carte?.start_date || !carte?.end_date) return false
  const picked = dayjs(date).startOf('day')
  const start = dayjs(carte.start_date).startOf('day')
  const end = dayjs(carte.end_date).startOf('day')
  return !picked.isBefore(start) && !picked.isAfter(end)
}

export const getCarteStockInitial = (carte) =>
  carte?.stock_initial ?? carte?.stock ?? null

export const getCarteStockAvailable = (carte) => {
  if (carte?.stock_available != null) return carte.stock_available
  const initial = getCarteStockInitial(carte)
  if (initial == null) return null
  return initial - Number(carte?.stock_out ?? 0)
}

export const getCarteStockConsumed = (carte) => Number(carte?.stock_out ?? 0)

export const getDayStockInitial = (day) => day?.stock_initial ?? day?.stock ?? 0

export const getDayStockAvailable = (day) =>
  day?.stock_available ?? getDayStockInitial(day) - Number(day?.stock_out ?? day?.used ?? 0)

export const getDayStockConsumed = (day) => Number(day?.stock_out ?? day?.used ?? 0)

export const getReservationAccessLabel = (row, t) => {
  if (row?.access_label) return row.access_label
  if (row?.access_mode === 'daily_choice') {
    return t ? t('cartesAcces.shared.accessMode.dailyChoice') : 'Jour au choix du client'
  }
  return t ? t('cartesAcces.shared.accessMode.interval') : "Chaque jour dans l'intervalle"
}

export const formatReservationAmountDetail = (row, t) => {
  if (row?.amount_breakdown) return row.amount_breakdown
  if (row?.pricing_type === 'per_pax' && row?.unit_price != null) {
    const price = Number(row.unit_price).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    const pax = row.pax ?? 1
    return t
      ? t('cartesAcces.shared.pricing.perPaxDetail', { price, pax })
      : `${price} DT × ${pax} pers.`
  }
  return t ? t('cartesAcces.shared.pricing.flat') : 'Forfait'
}

export const validateCarteReservationDate = (carte, date, t) => {
  if (!String(date || '').trim()) {
    return t ? t('cartesAcces.recherche.validation.dateRequired') : 'La date est obligatoire.'
  }
  const picked = dayjs(date).startOf('day')
  const today = dayjs().startOf('day')
  if (carte?.type === CARD_TYPE.ACCES && picked.isBefore(today)) {
    return t ? t('cartesAcces.recherche.validation.datePast') : 'La date ne peut pas être dans le passé.'
  }
  if (!carte?.start_date || !carte?.end_date) return null
  if (isDateInCarteInterval(carte, date)) return null
  const start = formatDateFR(carte.start_date)
  const end = formatDateFR(carte.end_date)
  return t
    ? t('cartesAcces.recherche.validation.dateOutOfRange', { start, end })
    : `La date doit être comprise entre le ${start} et le ${end}.`
}
